import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { ProductForm } from '@/interfaces/components/configurator/ProductForm';
import ChecklistSegmentModule from '@/components/views/productView/ChecklistSegmentModule.vue';
import PriceModule from '@/components/views/project/PriceModule.vue';
import { PriceItem } from '@/interfaces/general/PriceItem';
import { EventBus } from '@/helpers/EventBusHelper';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import { ActiveProductFormValueObject } from '@/interfaces/general/ActiveProductFormValueObject';
import { LoadingOverlayHelper } from '@/helpers/LoadingOverlayHelper';
import { PriceInputObject } from '@/interfaces/components/configurator/PriceInputObject';
import { RouteNames } from '@/enums/routes/RouteNames';
import { ConfiguratorValue } from '@/interfaces/components/configurator/ConfiguratorValue';
import { ChecklistSegment } from '@/interfaces/components/configurator/ChecklistSegment';
import { extractDefaultValues, extractUpdateFunctions } from '@/helpers/FormDataHelper';
import { debounce } from 'vue-debounce';
import { determineFieldVisibility, extractUpdateFunctionValues } from '@/helpers/FieldFunctionHelper';
import { UpdateFunctionEntry } from '@/interfaces/components/configurator/UpdateFunctionEntry';
import AddOfferItem from '@/components/views/productView/AddOfferItem.vue';
import OfferItem from '@/models/OfferItem';
import { ChecklistField } from '@/interfaces/components/configurator/ChecklistField';
import JsFunctionRepository from '@/repositories/JsFunctionRepository';
import JsFunction from '@/models/JsFunction';
import { parseAndTransformStringWithDynamicValues, registerJsonLogicFunctions } from '@/helpers/JsonLogicHelper';
import ClientRepository from '@/repositories/ClientRepository';
import { UserRepository } from '@/repositories/UserRepository';
import Client from '@/models/Client';
import { mapOfferItemsFormData, setDefaultPricelistValue } from '@/helpers/project/ProductFormVersionHelper';
import PopupNotifications from '@/modules/Notifications/components/PopupNotifications.vue';
import DefaultValueWarnings from '@/components/global/popup/DefaultValueWarnings.vue';
import { PopupNotificationOptions } from '@/modules/Notifications/interfaces/PopupNotificationOptions';
import Offer from '@/models/Offer';
import OfferRepository from '@/repositories/OfferRepository';
import { checkForErrorsInFieldsAndReturnState, resetHelperState } from '@/helpers/FormValidationHelper';
import { ErrorOptions } from '@/interfaces/ErrorOptions';
import { extractInvalidDefaultValues } from '@/helpers/FieldFunctionHelper';
import { defaultValueWarnings as defaultValueWarningsConfig } from '@/interfaces/components/projectNew/DefaultValueWarnings';
import HelpPage from '@/shared/components/HelpPage.vue';
import { useUnsavedDataGuard } from '@/shared/composables/useUnsavedDataGuard';
import router from '@/router';
import store from '@/store';

/* === Props & Emits === */
interface Props {
    wizardData?: any | null;
    shouldOpenDimensionsWizardConfigurator?: boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ProductView',
  props: {
    wizardData: { default: null },
    shouldOpenDimensionsWizardConfigurator: { type: Boolean, default: false }
  },
  emits: ["activeProductFormIdChanged"],
  setup(__props: any, { emit }: { emit: ({
    (event: 'activeProductFormIdChanged', productFormId: number | null): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { wizardData: any | null, shouldOpenDimensionsWizardConfigurator: boolean };





const { proxy } = getCurrentInstance()!;
const componentProxy = proxy as any;

/* === Configurator State === */
const notifications = ref<PopupNotificationOptions[]>([]);
const currentProductForm = ref<ProductForm | null>(null);
const sellingPriceList = ref<PriceItem[]>([]);
const purchasePriceList = ref<PriceItem[]>([]);
const roltekPriceList = ref<PriceItem[]>([]);
const loadingOverlay = new LoadingOverlayHelper(proxy, {});
const activeProductFormId = ref<number | null>(null);
const updateFunctions = ref<UpdateFunctionEntry[]>([]);
const isEditMode = ref(false);
const clientId = ref<string | null>(null);
const offerId = ref<string | null>(null);
const offerItemId = ref<string | null>(null);
const pIdFieldErrors = ref<string[]>([]);
const hasErrors = ref(false);
const hasUnsavedData = ref(false);
const level = ref(2);
const showAllErrors = ref(false);
const showAllErrorsContainer = ref(false);
const errorList = ref<any[]>([]);
const connection = ref<any>(null);
const production = ref(false);
const formFieldErrorsExist = ref(false);
const projectId = ref('');
const showDefaultValueWarningsModal = ref(true);
const temporaryDisabledPIDs = ref<string[]>([]);
const addOfferItem = ref<any>(null);

useUnsavedDataGuard(hasUnsavedData);

const getSingleProductForm = (productFormId: string) =>
    store.dispatch('configurator/getSingleProductForm', productFormId);
const addActiveProductForm = (activeProductForm: ActiveProductFormValueObject): Promise<number | null> =>
    store.dispatch('configurator/addActiveProductForm', activeProductForm);
const productForm = (productFormId: string): ProductForm | null =>
    store.getters['configurator/productForm'](productFormId);
const calculatePrice = (payload: PriceInputObject): Promise<any> =>
    store.dispatch('configurator/calculatePrice', payload);
const checkForErrors = (payload: any): Promise<any> => store.dispatch('configurator/checkForErrors', payload);
const resetActiveProductForms = () => store.commit('configurator/resetActiveProductForms');
const updateActiveProductFormValue = (payload: { pId: string; value: ConfiguratorValue; productFormId: number }) =>
    store.dispatch('configurator/updateActiveProductFormValue', payload);
const getProductFormValues = (productFormId: number): ActiveProductFormValueObject | null =>
    store.getters['configurator/activeProductForm'](productFormId);

/* === Derived Configurator Data === */
const productSystemName = computed(() =>
    props.wizardData ? props.wizardData.productSystemName : (router.currentRoute.query.productSystemName as string),
);
const userGroup = computed(() => store.getters['jwtData/currentUserGroup']);
const productDetails = computed(() => {
    if (currentProductForm.value == null) return null;
    return {
        productName: currentProductForm.value.matchcode,
        productCategory: currentProductForm.value.directory,
        productSystemName: productSystemName.value,
    };
});
const client = computed(() => (clientId.value == null ? null : ClientRepository.getById(clientId.value)));
const offer = computed(() => (offerId.value == null ? null : OfferRepository.getOfferById(offerId.value)));
const offerItem = computed<OfferItem | null>(() => {
    if (offerItemId.value == null || !offer.value) return null;
    return offer.value.offerItems.find((item: OfferItem) => item.id === offerItemId.value) ?? null;
});
const offerItemHasErrors = computed(() => !!offerItem.value?.hasErrors);
const clientsPaymentTypeId = computed(() => (client.value?.paymentType == null ? null : client.value.paymentType.id));
const currencySymbol = computed(() => UserRepository.getCurrentUsersCurrencySymbol());
const functions = computed(() => JsFunctionRepository.getAll());
const checklistSegments = computed(() => {
    if (currentProductForm.value == null) return [];
    return currentProductForm.value.checklists.checklistSegments.filter(
        (checklistSegment: ChecklistSegment) => !checklistSegment.isLocked,
    );
});
const productFormValues = computed(() =>
    activeProductFormId.value == null ? null : getProductFormValues(activeProductFormId.value),
);
const checklistErrors = computed(() => {
    if (
        !currentProductForm.value?.checklists?.checklistSegments ||
        currentProductForm.value.checklists.checklistSegments.length <= 0
    )
        return [];
    return currentProductForm.value.checklists.checklistSegments[0].checklistFields.filter(
        (checklistField: ChecklistField) =>
            (checklistField.fieldType === 'warning' || checklistField.fieldType === 'abort') &&
            productFormValues.value &&
            determineFieldVisibility(checklistField.checklistVisibleFunction, productFormValues.value),
    );
});
const checklistFieldErrorsExist = computed(
    () => pIdFieldErrors.value.length > 0 || checklistErrors.value.some((error) => error.fieldType === 'abort'),
);
const flattenedFields = computed(() => checklistSegments.value.map((segment) => segment.checklistFields).flat());
const defaultValueWarnings = computed(() => {
    const warnings = store.getters['configurator/defaultValueWarnings'];
    if (!warnings.length) return null;
    if (warnings?.length > 1) {
        return [
            warnings.find((warning: defaultValueWarningsConfig) => warning.productFormId === activeProductFormId.value),
        ];
    }
    return [warnings[0]];
});
const isCurrentUserTechnologist = computed(() => UserRepository.isCurrentUserTechnologist());

/* === Configurator Actions === */
function setDefaultValueWarnings() {
    const warnings: Array<{}> = extractInvalidDefaultValues(
        flattenedFields.value,
        productFormValues.value,
        activeProductFormId.value,
    );
    store.dispatch('configurator/setDefaultValueWarnings', [
        {
            id: offerItem.value?.displayRowNumber ?? activeProductFormId.value,
            warnings,
            productName: currentProductForm.value?.name,
            productId: currentProductForm.value?.id,
            productFormId: activeProductFormId.value,
        },
    ]);
}

function switchLevel(newLevel: number) {
    level.value = -1;
    nextTick(() => {
        level.value = newLevel;
    });
}
function handleIsConnected(event: any) {
    connection.value = event;
}
function handleIsProduction(event: any) {
    production.value = event;
}
function triggerVisibilityFunctions() {
    EventBus.$emit(EventBusEvents.triggerVisibilityFunctions, {
        activeProductFormValues: productFormValues.value,
    });
}
function triggerOptionsFunctions() {
    EventBus.$emit(EventBusEvents.triggerOptionsFunctions, {
        activeProductFormValues: productFormValues.value,
        activeProductFormId: activeProductFormId.value,
    });
}

async function checkForConfiguratorErrors(values: ActiveProductFormValueObject | null) {
    if (currentProductForm.value == null || values == null) return;
    try {
        const errors = await checkForErrors({
            activeProductFormValues: values,
            productFormId: currentProductForm.value.id,
            offerItemId: offerItemId.value,
        });
        errorList.value = errors.errors;
        hasErrors.value = !!errorList.value.length;
        showAllErrorsContainer.value = true;
    } catch (e) {
        if (Array.isArray(e)) addNotification(componentProxy.$t('Error occured'), e[0] as string, 'error');
    }
}

async function calculateConfiguratorPrice(values: ActiveProductFormValueObject | null) {
    pIdFieldErrors.value = [];
    if (currentProductForm.value == null || values == null) {
        sellingPriceList.value = [];
        return;
    }
    let calculatedSellingPriceList: PriceItem[] = [];
    try {
        const prices = await calculatePrice({
            activeProductFormValues: values,
            productFormId: currentProductForm.value.id,
            clientId: clientId.value,
            currencySymbol: currencySymbol.value,
            offerItemId: offerItemId.value ?? undefined,
            includePurchasePrice: true,
            projectId: projectId.value,
        });
        calculatedSellingPriceList = prices.sellingPrices;
        purchasePriceList.value = prices.purchasePrices;
        roltekPriceList.value = prices.roltekPrices;
    } catch (e) {
        if (Array.isArray(e)) addNotification(componentProxy.$t('Error occured'), e[0] as string, 'error');
    }
    sellingPriceList.value = calculatedSellingPriceList;
    showAllErrorsContainer.value = false;
}

async function getCurrentProductForm(): Promise<ProductForm | null> {
    const productFormId = props.wizardData
        ? props.wizardData.productFormId
        : String(router.currentRoute.params.productFormId);
    try {
        await getSingleProductForm(productFormId);
    } catch (e) {
        const errorObj = e as ErrorOptions;
        addNotification(
            componentProxy.$t('Error occured'),
            errorObj.response ? errorObj.response.data.meta.message : (errorObj.message as string),
            'error',
        );
        loadingOverlay.stop();
        router.push({ name: RouteNames.error });
        return null;
    }
    return productForm(productFormId);
}

async function getProperRouteOfferItem(routeOfferItemId: string): Promise<void> {
    try {
        await OfferItem.getById(routeOfferItemId);
    } catch (e) {
        loadingOverlay.stop();
        const errorObj = e as ErrorOptions;
        addNotification(
            componentProxy.$t('Error occured'),
            errorObj.response ? errorObj.response.data.meta.message : (errorObj.message as string),
            'error',
        );
        router.back();
    }
    return Promise.resolve();
}

function updateUnsavedDataState({ state }: { state: boolean }) {
    hasUnsavedData.value = state;
}

function addNotification(title: string, description: string, errorType: 'error' | 'warning') {
    notifications.value.push({
        title: componentProxy.$t(title),
        description: componentProxy.$t(description),
        errorType,
    });
}

function setConfiguratorLevel() {
    level.value = ['1', '2', '3'].includes(String(userGroup.value)) ? 2 : 0;
}

function validateFormFields() {
    if (!activeProductFormId.value && activeProductFormId.value !== 0) return;
    formFieldErrorsExist.value = false;
    const rowValues = getProductFormValues(activeProductFormId.value);
    const errorState = checkForErrorsInFieldsAndReturnState([
        {
            rowKey: null,
            values: rowValues ?? {},
            fieldSegments: flattenedFields.value as any,
        },
    ]);
    formFieldErrorsExist.value = errorState.hasErrors;
    if (errorState.hasErrors) showErrorState(errorState.errorDescription);
    EventBus.$emit(EventBusEvents.formValidationFinished);
}

function showErrorState(description: string) {
    componentProxy.$notification.error({
        message: componentProxy.$t('Invalid characters detected in form fields:') as string,
        description: description as string,
    });
}

/* === Initial Configurator Load === */
onMounted(async () => {
    resetHelperState();
    const productId = props.wizardData ? props.wizardData.productId : router.currentRoute.params.productId;
    clientId.value = props.wizardData
        ? props.wizardData.clientId
        : (router.currentRoute.query.clientId as string | null);
    offerId.value = props.wizardData ? props.wizardData.offerId : (router.currentRoute.query.offerId as string);
    offerItemId.value = props.wizardData ? props.wizardData.offerItemId : router.currentRoute.params.offerItemId;
    projectId.value = props.wizardData ? '' : (router.currentRoute.query.projectId as string);
    setConfiguratorLevel();
    if (props.wizardData) level.value = -1;
    loadingOverlay.start();
    EventBus.$on(EventBusEvents.changesInDataMade, updateUnsavedDataState);
    if (functions.value.length <= 0) {
        try {
            await JsFunction.getAll();
        } catch (e) {
            return e;
        }
    }
    if (clientId.value != null && !client.value) {
        try {
            await Client.getByIdForConfigurator(clientId.value);
        } catch {
            return;
        }
    }
    if (offerId.value && !offer.value) {
        try {
            await Offer.getById(offerId.value);
        } catch {
            return;
        }
    }
    if (offerItemId.value && !offerItem.value) {
        try {
            await getProperRouteOfferItem(offerItemId.value);
        } catch {
            return;
        }
    }
    currentProductForm.value = await getCurrentProductForm();
    if (currentProductForm.value == null) return;
    if (clientId.value == null || !client.value) {
        store.dispatch('configurator/determineDefaultPriceList', {
            data: checklistSegments.value,
            dataType: 'checklistSegments',
        });
    }
    if (router.currentRoute.params.alreadyInStore && router.currentRoute.params.alreadyInStore !== null) {
        activeProductFormId.value = parseInt(router.currentRoute.params.alreadyInStore, 10);
    } else {
        if (offerItem.value != null) {
            isEditMode.value = true;
            activeProductFormId.value = await addActiveProductForm(
                await extractDefaultValues({
                    productForm: currentProductForm.value,
                    productId,
                    clientId: clientId.value,
                    productSystemName: router.currentRoute.query.productSystemName,
                }),
            );
            await mapOfferItemsFormData(
                offerItem.value.offerItemFormData,
                activeProductFormId.value as number,
                clientId.value,
            );
        } else {
            const defaultProductFormValues = await extractDefaultValues({
                productForm: currentProductForm.value,
                productId,
                clientId: clientId.value,
                productSystemName: router.currentRoute.query.productSystemName,
            });
            activeProductFormId.value = await addActiveProductForm(defaultProductFormValues);
            await setDefaultPricelistValue(activeProductFormId.value as number);
        }
        emit('activeProductFormIdChanged', activeProductFormId.value);
    }
    updateFunctions.value = extractUpdateFunctions(currentProductForm.value);
    const customValues = store.getters['configurator/customValuesOverride'];
    if (props.wizardData) {
        await nextTick();
        setCustomFieldValues(props.wizardData?.customFieldValues);
    } else if (customValues.length) {
        await nextTick();
        setCustomFieldValues(customValues);
    }
    setDefaultValueWarnings();
    loadingOverlay.stop();
    EventBus.$on(EventBusEvents.updateSystemColorValue, updateColorValue);
});

function setCustomFieldValues(customValues: any) {
    if (activeProductFormId.value == null) return null;
    customValues.forEach((field: any) =>
        updateActiveProductFormValue({
            pId: field.pId,
            value: field.value,
            productFormId: activeProductFormId.value as number,
        }),
    );
}
function updateColorValue(payload: {
    activeProductFormId: number;
    colorSystemPID: string;
    colorCollection: string;
    colorDropdownPID: string;
    selectedColorValue: string | null;
    shouldFreezeManualInput?: boolean;
}) {
    if (payload.activeProductFormId !== activeProductFormId.value) return;
    updateActiveProductFormValue({
        pId: payload.colorSystemPID,
        value: payload.colorCollection,
        productFormId: payload.activeProductFormId,
    });
    if (payload.selectedColorValue) {
        nextTick(() =>
            updateActiveProductFormValue({
                pId: payload.colorDropdownPID,
                value: payload.selectedColorValue as string,
                productFormId: payload.activeProductFormId,
            }),
        );
        if (payload.shouldFreezeManualInput && !temporaryDisabledPIDs.value.includes(payload.colorDropdownPID))
            temporaryDisabledPIDs.value.push(payload.colorDropdownPID);
        if (!payload.shouldFreezeManualInput) {
            const index = temporaryDisabledPIDs.value.indexOf(payload.colorDropdownPID);
            if (index > -1) temporaryDisabledPIDs.value.splice(index, 1);
        }
    }
}

const debouncedPriceCalculation = debounce(
    (values: ActiveProductFormValueObject) => calculateConfiguratorPrice(values),
    300,
) as (values: ActiveProductFormValueObject) => void;
const debounceShowingChecklistErrors = debounce(() => showChecklistErrors(), 100);
const debounceTriggerVisibilityFunctions = debounce(() => triggerVisibilityFunctions(), 100);
const debounceTriggerOptionsFunctions = debounce(() => triggerOptionsFunctions(), 100);

function checkIfSelectedValueIsValid(id: string, value: ConfiguratorValue) {
    if (!defaultValueWarnings.value) return true;
    const field = flattenedFields.value.find((item) => item.pId === id);
    if (!field || field.fieldType !== 'dropdown') return true;
    const isValueAvailable = field.checklistDropdownField?.checklistDropdownOptions.some(
        (dropdownOption: any) => dropdownOption.vId === value,
    );
    if (isValueAvailable || !defaultValueWarnings.value) return true;
    const invalidValueWasDetectedAtMount = defaultValueWarnings.value[0].warnings.some(
        (warning: any) => warning.pId === id,
    );
    return !invalidValueWasDetectedAtMount;
}
function showChecklistErrors() {
    notifications.value = [];
    componentProxy.$notification.destroy();
    for (const checklistField of checklistErrors.value) {
        window.setTimeout(() => {
            if (checklistField.checklistStringField && checklistField.checklistStringField.defaultValue) {
                switch (checklistField.fieldType) {
                    case 'abort':
                        addNotification(
                            'Error happened',
                            parseAndTransformStringWithDynamicValues(
                                checklistField.checklistStringField.defaultValue,
                                checklistField.checklistStringField.functions,
                                productFormValues.value,
                                'function',
                            ),
                            'error',
                        );
                        break;
                    case 'warning':
                        addNotification(
                            'Warning',
                            parseAndTransformStringWithDynamicValues(
                                checklistField.checklistStringField.defaultValue,
                                checklistField.checklistStringField.functions,
                                productFormValues.value,
                                'function',
                            ),
                            'warning',
                        );
                        break;
                }
            }
        }, 50);
    }
}
function onProductFormValueChange(values: ActiveProductFormValueObject | null) {
    nextTick(() => {
        updateFunctions.value.forEach((entry: UpdateFunctionEntry) => {
            if (activeProductFormId.value == null) return;
            const updatedValues = extractUpdateFunctionValues(
                entry.updateFunctions,
                entry.type,
                values as ActiveProductFormValueObject,
            );
            if (updatedValues.length > 0) {
                if (!checkIfSelectedValueIsValid(entry.id, updatedValues[0])) return;
                updateActiveProductFormValue({
                    pId: entry.id,
                    value: updatedValues[0],
                    productFormId: activeProductFormId.value,
                });
            }
        });
        debounceTriggerVisibilityFunctions();
        debounceTriggerOptionsFunctions();
        if (!props.wizardData) debouncedPriceCalculation(values as ActiveProductFormValueObject);
        debounceShowingChecklistErrors();
    });
}
async function closeProductView() {
    componentProxy.$confirm({
        title: componentProxy.$t('Warning'),
        content: componentProxy.$t('Are you sure you want to close product view?'),
        okText: componentProxy.$t('Close'),
        cancelText: componentProxy.$t('Cancel'),
        onOk: () => {
            router.back();
        },
        onCancel() {},
    });
}
function closeModal() {
    showDefaultValueWarningsModal.value = false;
}
function onFunctionsChange() {
    registerJsonLogicFunctions(functions.value);
}

watch(productFormValues, onProductFormValueChange, {
    deep: true,
    immediate: true,
});
watch(functions, onFunctionsChange);
onBeforeUnmount(() => {
    EventBus.$off(EventBusEvents.changesInDataMade, updateUnsavedDataState);
    EventBus.$off(EventBusEvents.updateSystemColorValue, updateColorValue);
    notifications.value = [];
    componentProxy.$notification.destroy();
    store.dispatch('configurator/setDefaultValueWarnings', []);
    if (!props.shouldOpenDimensionsWizardConfigurator) store.dispatch('configurator/setCustomValuesOverride', []);
    if (!props.wizardData) resetActiveProductForms();
});

return { __sfc: true,props, emit, proxy, componentProxy, notifications, currentProductForm, sellingPriceList, purchasePriceList, roltekPriceList, loadingOverlay, activeProductFormId, updateFunctions, isEditMode, clientId, offerId, offerItemId, pIdFieldErrors, hasErrors, hasUnsavedData, level, showAllErrors, showAllErrorsContainer, errorList, connection, production, formFieldErrorsExist, projectId, showDefaultValueWarningsModal, temporaryDisabledPIDs, addOfferItem, getSingleProductForm, addActiveProductForm, productForm, calculatePrice, checkForErrors, resetActiveProductForms, updateActiveProductFormValue, getProductFormValues, productSystemName, userGroup, productDetails, client, offer, offerItem, offerItemHasErrors, clientsPaymentTypeId, currencySymbol, functions, checklistSegments, productFormValues, checklistErrors, checklistFieldErrorsExist, flattenedFields, defaultValueWarnings, isCurrentUserTechnologist, setDefaultValueWarnings, switchLevel, handleIsConnected, handleIsProduction, triggerVisibilityFunctions, triggerOptionsFunctions, checkForConfiguratorErrors, calculateConfiguratorPrice, getCurrentProductForm, getProperRouteOfferItem, updateUnsavedDataState, addNotification, setConfiguratorLevel, validateFormFields, showErrorState, setCustomFieldValues, updateColorValue, debouncedPriceCalculation, debounceShowingChecklistErrors, debounceTriggerVisibilityFunctions, debounceTriggerOptionsFunctions, checkIfSelectedValueIsValid, showChecklistErrors, onProductFormValueChange, closeProductView, closeModal, onFunctionsChange, ChecklistSegmentModule, PriceModule, AddOfferItem, PopupNotifications, DefaultValueWarnings, HelpPage }
}

})